<template>
  <BContainer
    fluid
    class="mb-4"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5
        class="fw-bold mb-0"
        style="color: #1e293b;"
      >
        My Violations
      </h5>
    </div>

    <BCard class="border shadow-sm">
      <BCardText>
        <BOverlay
          :show="state.loading"
          opacity="0.5"
        >
          <div class="d-md-none">
            <div
              v-if="violations.length === 0"
              class="text-center py-4"
            >
              <i
                class="fas fa-check-circle mb-2"
                style="font-size: 2rem; color: #10b981;"
              />
              <p
                class="small mb-0"
                style="color: #94a3b8;"
              >
                No violation records found
              </p>
            </div>

            <div
              v-for="item in violations"
              :key="item.id"
              class="border rounded p-3 mb-2 bg-white"
            >
              <div class="d-flex justify-content-between align-items-start mb-1">
                <strong>{{ item.violation_type ? item.violation_type.name : 'N/A' }}</strong>
                <BBadge :variant="statusVariant(item.status)">
                  {{ item.status }}
                </BBadge>
              </div>

              <div class="small text-muted mb-2">
                {{ item.occurred_at ? dateTimeShortFormatter(item.occurred_at) : 'N/A' }}
              </div>

              <div class="small mb-2">
                {{ item.description || 'No description' }}
              </div>

              <div class="d-flex gap-2">
                <BButton
                  size="sm"
                  variant="outline-secondary"
                  @click.prevent="openViolationDetails(item)"
                >
                  Details
                </BButton>
                <BButton
                  v-if="item.evidence_url"
                  size="sm"
                  variant="outline-primary"
                  :disabled="state.loadingEvidence"
                  @click.prevent="openEvidencePreview(item)"
                >
                  Evidence
                </BButton>
              </div>
            </div>
          </div>

          <BTable
            class="d-none d-md-table mb-0"
            :items="violations"
            :fields="tableFields"
            responsive
            stacked="md"
            show-empty
            hover
          >
            <template #empty>
              <div class="text-center py-4">
                <i
                  class="fas fa-check-circle mb-2"
                  style="font-size: 2rem; color: #10b981;"
                />
                <p
                  class="small mb-0"
                  style="color: #94a3b8;"
                >
                  No violation records found
                </p>
              </div>
            </template>

            <template #cell(violation_type)="{ item }">
              {{ item.violation_type ? item.violation_type.name : '—' }}
            </template>

            <template #cell(severity_level)="{ item }">
              <BBadge
                v-if="item.violation_type"
                :variant="severityVariant(item.violation_type.severity_level)"
              >
                {{ item.violation_type.severity_level }}
              </BBadge>
              <span v-else>—</span>
            </template>

            <template #cell(status)="{ value }">
              <BBadge :variant="statusVariant(value)">
                {{ value }}
              </BBadge>
            </template>

            <template #cell(occurred_at)="{ value }">
              {{ value ? dateTimeShortFormatter(value) : '—' }}
            </template>

            <template #cell(evidence)="{ item }">
              <BButton
                v-if="item.evidence_url"
                size="sm"
                variant="outline-primary"
                :disabled="state.loadingEvidence"
                @click.prevent="openEvidencePreview(item)"
              >
                View
              </BButton>
              <span v-else>—</span>
            </template>
          </BTable>
        </BOverlay>

        <div
          v-if="pagination.last_page > 1"
          class="d-flex justify-content-end mt-3"
        >
          <BPagination
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            size="sm"
            @change="onPageChange"
          />
        </div>
      </BCardText>
    </BCard>

    <BModal
      ref="modalViolationDetails"
      title="Violation Details"
      centered
      hide-footer
    >
      <div v-if="selectedViolation">
        <p class="mb-2">
          <strong>Type:</strong> {{ selectedViolation.violation_type ? selectedViolation.violation_type.name : 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Severity:</strong> {{ selectedViolation.violation_type ? selectedViolation.violation_type.severity_level : 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Status:</strong> {{ selectedViolation.status || 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Date:</strong> {{ selectedViolation.occurred_at ? dateTimeShortFormatter(selectedViolation.occurred_at) : 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Location:</strong> {{ selectedViolation.location || 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Description:</strong> {{ selectedViolation.description || 'No description' }}
        </p>
        <BButton
          v-if="selectedViolation.evidence_url"
          size="sm"
          variant="outline-primary"
          :disabled="state.loadingEvidence"
          @click.prevent="openEvidencePreview(selectedViolation)"
        >
          View Evidence
        </BButton>
      </div>
    </BModal>

    <BModal
      ref="modalEvidencePreview"
      title="Violation Evidence"
      size="lg"
      centered
      hide-footer
    >
      <div
        v-if="state.loadingEvidence"
        class="text-center py-4"
      >
        <i class="fas fa-spinner fa-spin fa-2x text-primary" />
      </div>

      <div
        v-else-if="evidencePreviewUrl"
        class="text-center"
      >
        <img
          :src="evidencePreviewUrl"
          alt="Violation evidence"
          class="img-fluid rounded border"
        >
      </div>

      <div
        v-else
        class="text-center text-muted py-4"
      >
        No evidence image available.
      </div>
    </BModal>
  </BContainer>
</template>

<script>
import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import { SSharedViolation } from '@/services/shared'

export default {
  name: 'StudentViolations',

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'student'],

  metaInfo: () => ({ title: 'My Violations' }),

  data () {
    return {
      state: { loading: false, loadingEvidence: false },
      violations: [],
      pagination: { current_page: 1, per_page: 10, total: 0, last_page: 1 },
      evidencePreviewUrl: '',
      selectedViolation: null
    }
  },

  computed: {
    tableFields () {
      return [
        { key: 'violation_type', label: 'Type', sortable: false },
        { key: 'severity_level', label: 'Severity' },
        { key: 'description', label: 'Description' },
        { key: 'location', label: 'Location' },
        { key: 'evidence', label: 'Evidence' },
        { key: 'occurred_at', label: 'Date', sortable: true },
        { key: 'status', label: 'Status', sortable: true }
      ]
    }
  },

  mounted () {
    this.fetchViolations()
  },

  beforeDestroy () {
    if (this.evidencePreviewUrl) {
      URL.revokeObjectURL(this.evidencePreviewUrl)
      this.evidencePreviewUrl = ''
    }
  },

  methods: {
    statusVariant (status) {
      const v = { pending: 'warning', resolved: 'success', dismissed: 'secondary' }
      return v[status] || 'secondary'
    },

    severityVariant (level) {
      const v = { warning: 'info', minor: 'primary', moderate: 'warning', major: 'danger', severe: 'dark' }
      return v[level] || 'secondary'
    },

    onPageChange (page) {
      this.pagination.current_page = page
      this.fetchViolations()
    },

    async fetchViolations () {
      this.state.loading = true
      try {
        const { data } = await SSharedViolation.myViolations({
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        })
        const items = data.items || data.data || []
        this.violations = items
        this.pagination.total = data.total_rows || data.total || items.length
        this.pagination.per_page = data.per_page || data.pagination?.per_page || this.pagination.per_page
        this.pagination.current_page = data.current_page || data.pagination?.current_page || this.pagination.current_page
        this.pagination.last_page = data.last_page || data.pagination?.last_page || Math.max(1, Math.ceil(this.pagination.total / this.pagination.per_page))
      } catch {
        this.violations = []
      } finally {
        this.state.loading = false
      }
    },

    openViolationDetails (item) {
      this.selectedViolation = item
      this.$refs.modalViolationDetails.show()
    },

    getEvidenceFilename (evidenceUrl) {
      if (!evidenceUrl || typeof evidenceUrl !== 'string') return ''
      const parts = evidenceUrl.split('/')
      return parts.length ? parts[parts.length - 1] : ''
    },

    async openEvidencePreview (violation) {
      const filename = this.getEvidenceFilename(violation?.evidence_url)
      if (!filename) {
        if (this.evidencePreviewUrl) {
          URL.revokeObjectURL(this.evidencePreviewUrl)
        }
        this.evidencePreviewUrl = ''
        this.$refs.modalEvidencePreview.show()
        return
      }

      this.state.loadingEvidence = true
      if (this.evidencePreviewUrl) {
        URL.revokeObjectURL(this.evidencePreviewUrl)
      }
      this.evidencePreviewUrl = ''
      this.$refs.modalEvidencePreview.show()

      try {
        const { data } = await SSharedViolation.getEvidence(filename)
        this.evidencePreviewUrl = URL.createObjectURL(data)
      } catch {
        this.evidencePreviewUrl = ''
      } finally {
        this.state.loadingEvidence = false
      }
    }
  }
}
</script>
